import { ENUM_DATA } from "../../constData/enum";
import { convertToDDMMYYYY } from "../../helpers";

const getShipmentAllocationValue = (
  value,
  blankValue,
  preserveUndefinedShipmentAllocations
) => {
  if (preserveUndefinedShipmentAllocations) {
    return value === null ? blankValue : value;
  }

  return value ?? blankValue;
};

export const mapDrmBaseFields = (
  item,
  {
    blankValue,
    preserveUndefinedShipmentAllocations = false,
  }
) => ({
  id: item?.id ?? blankValue,
  sagaId: item?.sagaId ?? blankValue,
  carrierScac: item?.carrierScac ?? blankValue,
  carrierName: item?.carrierName ?? blankValue,
  serviceScac: item?.serviceScac ?? blankValue,
  tariffNumber: item?.tariffNumber ?? blankValue,
  tariffId: item?.tariffId ?? blankValue,
  tariffDetails: item?.tariffDetails ?? blankValue,
  equipmentType: item?.equipmentType ?? blankValue,
  laneDescription: item?.laneDescription ?? blankValue,
  originZone: item?.originZone ?? blankValue,
  originZoneDesc: item?.originZoneDesc ?? blankValue,
  originZoneShortDesc: item?.originZoneShortDesc ?? blankValue,
  originCity: item?.originCity ?? blankValue,
  originState: item?.originState ?? blankValue,
  originCountry: item?.originCountry ?? blankValue,
  originPostalCode: item?.originPostalCode ?? blankValue,
  destinationZone: item?.destinationZone ?? blankValue,
  destinationZoneDesc: item?.destinationZoneDesc ?? blankValue,
  destinationZoneShortDesc: item?.destinationZoneShortDesc ?? blankValue,
  destinationCity: item?.destinationCity ?? blankValue,
  destinationState: item?.destinationState ?? blankValue,
  destinationPostalCode: item?.destinationPostalCode ?? blankValue,
  destinationCountry: item?.destinationCountry ?? blankValue,
  lineHaulChargeType: item?.lineHaulChargeType ?? blankValue,
  effectiveDate: item?.effectiveDate ?? blankValue,
  expirationDate: item?.expirationDate ?? blankValue,
  awardBackup: item?.awardBackup ?? blankValue,
  qtyActiveFlag: item?.qtyActiveFlag ?? blankValue,
  minShipmentsAllocated: getShipmentAllocationValue(
    item?.minShipmentsAllocated,
    blankValue,
    preserveUndefinedShipmentAllocations
  ),
  maxShipmentsAllocated: getShipmentAllocationValue(
    item?.maxShipmentsAllocated,
    blankValue,
    preserveUndefinedShipmentAllocations
  ),
  percentageActiveFlag: item?.percentageActiveFlag ?? blankValue,
});

const mapErrors = (errors, blankValue) =>
  errors?.length > 0
    ? errors.map((error) => ({
        record: error.columnName ?? blankValue,
        errorMessage: error.errorMessage ?? blankValue,
      }))
    : [];

const mapLineHaulRateFields = (item) => {
  const rateFields = {
    lineHaulRate: item?.lineHaulRate ?? ENUM_DATA.Blank,
    baf: item?.baf ?? ENUM_DATA.Blank,
    civa: item?.civa ?? ENUM_DATA.Blank,
    ets: item?.ets ?? ENUM_DATA.Blank,
  };

  switch (item?.lineHaulChargeType) {
    case "BAF":
      return { ...rateFields, lineHaulRate: rateFields.baf };
    case "CIVA":
      return { ...rateFields, lineHaulRate: rateFields.civa };
    case "ETS":
      return { ...rateFields, lineHaulRate: rateFields.ets };
    default:
      return rateFields;
  }
};

export const mapChangeLogRecord = (
  item,
  {
    includeRawDailyDates = false,
    preserveUndefinedShipmentAllocations = false,
  } = {}
) => ({
  ...mapDrmBaseFields(item, {
    blankValue: ENUM_DATA.Blank,
    preserveUndefinedShipmentAllocations,
  }),
  minCarrierAllocationPercentage:
    item?.minCarrierAllocationPercentage == null
      ? ENUM_DATA.Blank
      : item.minCarrierAllocationPercentage,
  maxCarrierAllocationPercentage:
    item?.maxCarrierAllocationPercentage == null
      ? ENUM_DATA.Blank
      : item.maxCarrierAllocationPercentage,
  modeOfTransport: item?.modeOfTransport ?? ENUM_DATA.Blank,
  status: item?.status ?? ENUM_DATA.Blank,
  message: item?.message ?? ENUM_DATA.Blank,
  region: item?.region ?? ENUM_DATA.Blank,
  carrierManager: item?.carrierManager ?? ENUM_DATA.Blank,
  primaryCustomers: item?.primaryCustomers ?? ENUM_DATA.Blank,
  versionNumber: item?.versionNumber ?? ENUM_DATA.Blank,
  errors: mapErrors(item?.errors, ENUM_DATA.Blank),
  userId: item?.userId ?? ENUM_DATA.Blank,
  createdDate: item?.createdDate ?? ENUM_DATA.Blank,
  updatedDate: item?.updatedDate ?? ENUM_DATA.Blank,
  defaultError: item?.defaultError ?? ENUM_DATA.Blank,
  rangeCode: item?.rangeCode ?? ENUM_DATA.Blank,
  rangeToUnits: item?.rangeToUnits ?? ENUM_DATA.Blank,
  rate: item?.rate ?? ENUM_DATA.Blank,
  qtyBucketType: item?.qtyBucketType ?? ENUM_DATA.Blank,
  pctBucketType: item?.pctBucketType ?? ENUM_DATA.Blank,
  ...(includeRawDailyDates
    ? {
        qtyStartDate: item?.qtyStartDate ?? ENUM_DATA.Blank,
        qtyEndDate: item?.qtyEndDate ?? ENUM_DATA.Blank,
        pctStartDate: item?.pctStartDate ?? ENUM_DATA.Blank,
        pctEndDate: item?.pctEndDate ?? ENUM_DATA.Blank,
      }
    : {}),
  miles: item?.miles ?? ENUM_DATA.Blank,
  orderType: item?.orderType ?? ENUM_DATA.Blank,
  preAwarded: item?.preAwarded ?? ENUM_DATA.Blank,
  btfRate: item?.btfRate ?? ENUM_DATA.Blank,
  destHub: item?.destHub ?? ENUM_DATA.Blank,
  orgHub: item?.orgHub ?? ENUM_DATA.Blank,
  changeComment: item?.changeComment ?? ENUM_DATA.Blank,
  itemType: item?.itemType ?? ENUM_DATA.Blank,
  item: item?.item ?? ENUM_DATA.Blank,
  estimatedMileagePcMiler: item?.estimatedMileagePcMiler ?? ENUM_DATA.Blank,
  annualShipmentVolume: item?.annualShipmentVolume ?? ENUM_DATA.Blank,
  availableAnnualShipmentVolume:
    item?.availableAnnualShipmentVolume ?? ENUM_DATA.Blank,
  annualMaxShipmentVolume: item?.annualMaxShipmentVolume ?? ENUM_DATA.Blank,
  bidRateWoFuel: item?.bidRateWoFuel ?? ENUM_DATA.Blank,
  laneServiceType: item?.laneServiceType ?? ENUM_DATA.Blank,
  incoterm: item?.incoterm ?? ENUM_DATA.Blank,
  supplier: item?.supplier ?? ENUM_DATA.Blank,
  transitTime: item?.transitTime ?? ENUM_DATA.Blank,
  originPortCountry: item?.originPortCountry ?? ENUM_DATA.Blank,
  originPort: item?.originPort ?? ENUM_DATA.Blank,
  destinationTransportationMode:
    item?.destinationTransportationMode ?? ENUM_DATA.Blank,
  tradeLane: item?.tradeLane ?? ENUM_DATA.Blank,
  destinationPort: item?.destinationPort ?? ENUM_DATA.Blank,
  destinationPortCountry: item?.destinationPortCountry ?? ENUM_DATA.Blank,
  bucketType: item?.bucketType ?? ENUM_DATA.Blank,
  ...mapLineHaulRateFields(item),
  qtyStartDate: item?.effectiveDate
    ? convertToDDMMYYYY(item.effectiveDate)
    : ENUM_DATA.Blank,
  qtyEndDate: item?.expirationDate
    ? convertToDDMMYYYY(item.expirationDate)
    : ENUM_DATA.Blank,
  pctStartDate: item?.effectiveDate
    ? convertToDDMMYYYY(item.effectiveDate)
    : ENUM_DATA.Blank,
  pctEndDate: item?.expirationDate
    ? convertToDDMMYYYY(item.expirationDate)
    : ENUM_DATA.Blank,
  coupaFile: item?.coupaFile
    ? {
        id: item.coupaFile.id ?? ENUM_DATA.Blank,
        coupaFileName: item.coupaFile.coupaFileName ?? ENUM_DATA.Blank,
        status: item?.status ?? ENUM_DATA.Blank,
        coupaFileType: item.coupaFile.coupaFileType ?? ENUM_DATA.Blank,
        domesticCoupaFileData: null,
      }
    : ENUM_DATA.Blank,
  bidFileName: item?.coupaFile?.coupaFileName ?? ENUM_DATA.Blank,
});

export const parseChangeLogTableData = (data, options) => {
  if (!data?.details?.data?.length) {
    return [];
  }

  return data.details.data.map((item) => mapChangeLogRecord(item, options));
};
